import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listApiKeys } from "@/api/lookups";
import type { ApiKeyResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/table/DataTable";
import { ApiKeyForm } from "@/components/registries/ApiKeyForm";

export function ApiKeysAdmin() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const keys = useQuery({
    queryKey: ["api-keys", "list"],
    queryFn: () => listApiKeys(),
  });
  const [showForm, setShowForm] = useState(false);

  const columns: ColumnDef<ApiKeyResponse, unknown>[] = useMemo(
    () => [
      {
        id: "provider",
        header: t("adminApiKeys.columns.provider"),
        cell: (info) => info.row.original.provider,
      },
      {
        id: "updated_at",
        header: t("adminApiKeys.columns.updatedAt"),
        cell: (info) =>
          new Date(info.row.original.updated_at).toLocaleString("ru"),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">{t("adminApiKeys.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("adminApiKeys.subtitle")}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="open-upsert-api-key"
        >
          {t("adminApiKeys.upsertButton")}
        </Button>
      </header>
      <DataTable
        data={keys.data ?? []}
        columns={columns}
        testId="api-keys-table"
        isLoading={keys.isLoading}
      />
      <ApiKeyForm
        open={showForm}
        existing={keys.data ?? []}
        onClose={() => setShowForm(false)}
        afterSubmit={() =>
          queryClient.invalidateQueries({ queryKey: ["api-keys"] })
        }
      />
    </div>
  );
}
